import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@invent/wl-ui-kit-next/ui';
import { Check, ChevronDown, ChevronUp, PanelRight, X } from 'lucide-react';
import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { cn } from '@invent/wl-ui-kit-next';
import {
  CompactBar,
  CompactChipLine,
  CompactDivider,
  CompactFilters,
  CompactMenu,
  CompactSearch,
  CompactTitle,
  CondensedScope,
  DataTable,
  FilterChip,
  HelpPopover,
  NotPermitted,
  PageHeader,
  SelectChip,
  STICKY_BAR_TOP,
  STICKY_TABLE_TOP,
  useEscapeClose,
  useScrolledPast,
  useWorkToolbar,
  WorkToolbar,
  type DataTableState,
  type ICompactChip,
  type IDataColumn
} from '@/design';
import { useCan } from '@/domains/auth';
import { ddApi } from '../../api/dd-api';
import type { IDdInterview } from '../../api/dd-contracts';
import { internalFieldsApi } from '../../api/internal-fields-api';
import { listsApi } from '../../api/lists-api';
import type {
  IListAssignee,
  IListCell,
  IListCriteriaGroup,
  IListFieldCatalogue,
  IListQuery,
  IListRow,
  ListCriteriaNode
} from '../../api/lists-contracts';
import { LoadError, useAdminData } from '../../components/admin-page';
import { recordSurveyPath } from '../../components/participant-link';
import { StateCards, useCardVariant } from '../../components/state-cards';
import {
  DD_DISCLOSURE_COLUMNS,
  callOverdue,
  ddDisclosuresOf,
  ddStep,
  ddStepStateOf,
  ddWhen,
  ddWhenShort,
  meetingShortLabel,
  nextMeetingOf,
  qualityLabel,
  sameDay,
  u4FlagOf
} from '../../dd';
import { ALL_SURVEYS, LIST_PAGE_SIZES, countOf, listSurveyLabel } from '../../lists';
import { TEAM_PATHS } from '../../paths';
import { refusalMessage } from '../../refusal';
import { AssignAll, AssignAllSaid, type IAssignedAll, type IAssignedCell } from '../lists/assign-all';
import { cellText } from '../lists/cells';
import { AdvancedLink } from '../lists/filter-chips';
import { ASSIGNED_TO } from '../lists/editing';
import { rowKeyOf, useListRows } from '../lists/use-list-rows';
import {
  WorkAdvanced,
  WorkHeader,
  WorkSearch,
  quickChip,
  workFilterChips,
  workHeads,
  type IWorkScope
} from '../lists/work-page/controls';
import { WorkPageHeaderActions } from '../lists/work-page/header-actions';
import { PAGING, WorkPager, dropPaging, useWorkPaging } from '../lists/work-page/pager';
import { SelectionBar } from '../lists/work-page/selection-bar';
import { signalsOn, toggledSignals, useCountedFilter } from '../lists/work-page/signals';
import { useWorkPage, useWorkSearch } from '../lists/work-page/use-work-page';
import { DdBlock, U4Check, ddAnchor, type IDdRevealState } from '../person-hub/due-diligence';
import { listCellException } from '../person-hub/dd-exception';
import { DD_SIGNALS, useDdCards } from './card-signals';
import { COLUMNS_MULTI_REASON, COLUMNS_MULTI_WORDS, COLUMNS_REASON, useDdColumns } from './dd-columns';
import { DdOverdueChip, DdStatusChip, DdSurveyPicklist, DdSurveyToggles, U4FlagLink, surveysParam } from './dd-parts';

/**
 * Work → DD calls (SR-314, RNK-06; survey-api's due-diligence design §7): the survey's seeded
 * *DD list* — eligible to travel, the survey submitted, the DD call box not ticked (a tick holds
 * across cycles, SR-458) — read through the Lists engine, with nothing of its own deciding who is
 * on it.
 *
 * **Narrowing is the Lists engine's too.** *Whose* (the viewer's rows — the lists' `mine` scope —
 * the unassigned, everyone), the name, firm or CRD search and the quick views (*Not started*,
 * *In progress*, *Call scheduled*, *No outreach yet*, *Ready for a call*) are leaves ANDed onto the seeded view's
 * criteria, and each quick view's count is survey-api's `/lists/counts` under the same query. Lists'
 * own controls sit beside them (`work-page/`): a column's funnel, counted under the queue's own
 * narrowing, its filter ANDed on like the rest, its sort in place of the view's, and extra columns.
 * Nothing is filtered or counted here. *Next step* is `ddStep` over the row's cells — the one step
 * function the panel's button and the record's chip read (A4): the cadence (three DD emails, then a
 * call), a booking of any kind before it (`dd.next_meeting_*`, A1). The wide table draws the DD
 * call's booking in a column of its own — the date, who set it, the note and *Overdue* once its time
 * has passed (`callOverdue`) — and the soonest booking of any kind under *Meetings*.
 *
 * **Assignment is the Lists' own cell** (`sys.assigned_to`), written through `POST
 * /admin/lists/edits` at the version the row was read with — the grid's write, never a second
 * assignment model: one row from the side panel, the ticked rows from the bar above the table.
 * survey-api asks `internal_fields.write` and `lists.assign` of it; a reader without both is told
 * so beside the controls. A row a colleague assigned since the read is a conflict, said and left
 * as theirs, and the list is read again. *Assign all N…* (SR-459) assigns every row of the queue's
 * query — whose, quick view, search and states — in one mass update (`AssignAll`), said above the
 * queue with *Undo*. The State filter ANDs `sys.state in […]` on like the rest, counted per state by
 * `/aggregates` grouped under the other narrowing.
 *
 * **A row opens beside the queue** (Lists' reading pane's layout, Esc rule and ↑/↓ following the
 * focused row): the record page's own Due diligence block (`DdBlock`), with *Previous* / *Next*,
 * its *Mark as exception…* — a Quality rating reason written through the same Lists cell write
 * (`listCellException`), which survey-api turns into Travel NO. A completion there is said above the queue with *Undo*
 * (the interview's reopening). The advisor's name is a link to the block on the record page
 * (`IDdRevealState`), and `?open=<participantId>` opens a row's panel once the page holding it is
 * drawn — My tasks' way in (SR-424). A write from the panel or its sheet reads that one row again
 * and puts it in place (A5), never the page: a completed call shows Completed until *Refresh*, when
 * it has left the list. Nothing polls: Refresh
 * (Lists' and the ranking record's `RefreshButton`) reads the page again by hand.
 *
 * **The page scrolls, its table's header sticking** under the app bar, as Metrics updates' does:
 * once the cards and the filters have scrolled away, the compact bar (`condense.tsx`) draws them in
 * the app bar's row — the survey as a menu, the open card as one chip holding the others, the
 * search, Whose, State behind *Filters*, *Advanced…*, *Assign all*, *Columns* and the actions as
 * icons — and the filters applied on a second row, as under the toolbar. *Whose* is the scope the
 * rows are read in, said by its own control, never among the filters applied.
 */

/** One survey's seeded DD list: the view, and its query. */
interface IDdView {
  survey: string;
  viewId: string;
  query: IListQuery;
}

/** The queue's DD list — one survey's seeded view, or several surveys' joined (SR-499) — with
 *  each survey's own view (what a row's write names); `none` where no catalogue chosen holds one. */
type DdList = { viewId: Nullable<string>; query: IListQuery; views: IDdView[] } | 'none';

const SURVEY = 'sys.survey';

/** A view's own criteria as nodes to AND beside others: an AND root's children, any other root whole. */
function ownNodes(root: Nullable<IListCriteriaGroup> | undefined): ListCriteriaNode[] {
  if (!root || root.children.length === 0) return [];
  return root.op === 'and' ? root.children : [{ op: root.op, children: root.children }];
}

/** Several surveys' DD lists as one query over `all` (SR-499): a row is on it where its own
 *  survey's seeded criteria hold — each view's tree under its survey, ORed — the columns joined,
 *  the first view's order. Nothing here decides who is on a list; the seeds do. */
function joinedQuery(views: readonly IDdView[]): IListQuery {
  const columns = [...new Set(views.flatMap((view) => view.query.columns ?? []))];
  return {
    survey: ALL_SURVEYS.code,
    surveys: views.map((view) => view.survey),
    criteria: {
      version: 1,
      op: 'and',
      children: [
        {
          op: 'or',
          children: views.map((view) => ({
            op: 'and',
            children: [{ field: SURVEY, op: 'eq', value: view.survey }, ...ownNodes(view.query.criteria)]
          }))
        }
      ]
    },
    columns,
    sorts: views[0].query.sorts ?? null
  };
}

/** The row's cycle — what "this cycle" is in the panel — asked beside the view's columns. */
const CYCLE = 'sys.cycle';
const NAME = 'sys.display_name';
const STATE = 'sys.state';
const STATUS = 'dd.status';
const OUTREACH = 'dd.outreach_count';
const LAST_OUTREACH = 'dd.last_outreach_at';
/** SR-424: the call booked on the row cycle's interview, and who booked it — asked beside the
 *  view's columns, since the seeded view (planted once) does not name them. */
const SCHEDULED = 'dd.scheduled_at';
const SCHEDULED_BY = 'dd.scheduled_by';
const SCHEDULE_NOTE = 'dd.schedule_note';
/** A1: the cycle's soonest open booking of any kind, and its kind — survey-api's projection. */
const NEXT_MEETING_AT = 'dd.next_meeting_at';
const NEXT_MEETING_KIND = 'dd.next_meeting_kind';
/** When this cycle's DD call was confirmed clear — what *Next step* reads as cleared. */
const DISCLOSURES_CLEARED = 'dd.disclosures_cleared_at';
const PARTICIPANT = 'sys.participant_id';
const FIRM = 'sys.firm_name';
const IDENTIFIER = 'sys.identifier';
const CITY = 'sys.city';

/** The queue's own columns' fields — *Columns* offers the rest of the catalogue. */
const FIXED = [NAME, FIRM, CITY, STATE, STATUS, SCHEDULED, NEXT_MEETING_AT, OUTREACH, LAST_OUTREACH, ASSIGNED_TO];

/** What the queue leaves to the panel while one is open beside it — and the viewer's added fields. */
const COMPACT_HIDES = new Set(['firm', 'place', 'quality', 'scheduled', 'meetings', 'disclosures']);

/** The Quality column's header: a row leaves the DD list as its DD call completes, so the rating
 *  it shows is the one the last cycle's call wrote. */
const QUALITY_LAST = 'Quality (last cycle)';

/** The assignee select's value for nobody: no account id spells it. */
const NOBODY = 'nobody';

/** Why the assignment controls are off, said once beside them (`NotPermitted`'s id). */
const ASSIGN_REASON = 'dd-assign-not-permitted';

type Whose = 'mine' | 'unassigned' | 'all';
const WHOSE: ReadonlyArray<{ value: Whose; label: string }> = [
  { value: 'mine', label: 'Assigned to me' },
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'all', label: 'Everyone' }
];

type Show = 'all' | 'not_started' | 'in_progress' | 'scheduled' | 'no_outreach' | 'call';

/** The quick views, each a set of leaves on the queue's query. An outreach count of none is a
 *  missing cell or a 0, so *No outreach yet* asks for either. `label` is the card's, short so the
 *  six cards hold one row at 1366 px with four-digit counts; `full` is said in its tooltip and help. */
const SHOWS: ReadonlyArray<{ value: Show; label: string; full?: string; hint: string; leaves: ListCriteriaNode[] }> = [
  { value: 'all', label: 'All', hint: 'the whole DD list', leaves: [] },
  {
    value: 'not_started',
    label: 'Not started',
    hint: 'no interview yet',
    leaves: [{ field: STATUS, op: 'eq', value: 'none' }]
  },
  {
    value: 'in_progress',
    label: 'In progress',
    hint: 'interview open or in review',
    leaves: [{ field: STATUS, op: 'in', value: ['in_progress', 'pending_review'] }]
  },
  // SR-424: the rows whose call is booked — past ones too, which the column marks overdue.
  {
    value: 'scheduled',
    label: 'Booked',
    full: 'Call scheduled',
    hint: 'call booked',
    leaves: [{ field: SCHEDULED, op: 'not_empty' }]
  },
  {
    value: 'no_outreach',
    label: 'No outreach',
    full: 'No outreach yet',
    hint: 'no outreach logged',
    leaves: [
      {
        op: 'or',
        children: [
          { field: OUTREACH, op: 'empty' },
          { field: OUTREACH, op: 'eq', value: 0 }
        ]
      }
    ]
  },
  {
    value: 'call',
    label: 'Ready',
    full: 'Ready for a call',
    hint: '3 emails sent, ready to dial',
    leaves: [
      { field: STATUS, op: 'eq', value: 'none' },
      { field: OUTREACH, op: 'gte', value: 3 }
    ]
  }
];

/** The address with `show` chosen — *All* is none — the page's own way of writing it. */
function withShow(params: URLSearchParams, show: Show): URLSearchParams {
  const next = dropPaging(new URLSearchParams(params));
  if (show === 'all') next.delete('show');
  else next.set('show', show);
  return next;
}

/** What *Clear all* drops: the search, the quick view, the states and Lists' filter. */
const CLEARED = ['q', 'show', 'states', 'f'];

/** Whether a rows read's `asked` (`useListRows`) is the chosen surveys'. */
function answersSurveys(asked: string, surveys: readonly string[]): boolean {
  const { query } = JSON.parse(asked) as { query: IListQuery };
  const read = query.survey === ALL_SURVEYS.code ? (query.surveys ?? []) : [query.survey];
  return read.join('+') === surveys.join('+');
}

function whoseOf(params: URLSearchParams): Whose {
  const asked = params.get('whose');
  return asked === 'all' || asked === 'unassigned' ? asked : 'mine';
}

function showOf(params: URLSearchParams): Show {
  return SHOWS.find((show) => show.value === params.get('show'))?.value ?? 'all';
}

/** `?states=` — the states the queue is narrowed to, one key each: a state's name may hold a comma. */
function statesOf(params: URLSearchParams): string[] {
  return params.getAll('states').filter((state) => state !== '');
}

/** The State filter's leaf, none while no state is chosen (SR-459). */
function stateLeaves(states: readonly string[]): ListCriteriaNode[] {
  return states.length > 0 ? [{ field: STATE, op: 'in', value: [...states] }] : [];
}

/** How the assignments name the queue's rows. */
const ADVISORS = { one: 'advisor', many: 'advisors' };

/** The seeded view's query with the screen's narrowing ANDed on: the view's own tree kept whole
 *  (its children, where it is an AND), the scope `mine` for *Assigned to me* only. */
function narrowed(base: IListQuery, whose: Whose, q: string, leaves: readonly ListCriteriaNode[]): IListQuery {
  const own = ownNodes(base.criteria);
  const added: ListCriteriaNode[] = [
    ...(whose === 'unassigned' ? [{ field: ASSIGNED_TO, op: 'empty' } as const] : []),
    // A name, firm or CRD: Reinstatement's search, the three ORed.
    ...(q !== '' ? [searchLeaf(q)] : []),
    ...leaves
  ];
  const criteria: IListCriteriaGroup = { version: 1, op: 'and', children: [...own, ...added] };
  return { ...base, criteria, scope: { ...(base.scope ?? {}), mine: whose === 'mine' } };
}

function searchLeaf(q: string): ListCriteriaNode {
  return { op: 'or', children: [NAME, FIRM, IDENTIFIER].map((field) => ({ field, op: 'contains', value: q })) };
}

/** The record's survey tab, landing on its Due diligence block (`IDdRevealState`). */
function recordLink(row: IListRow) {
  return {
    to: recordSurveyPath(row.participantId, row.survey),
    state: { revealDd: ddAnchor(row.participantId, row.survey) } satisfies IDdRevealState
  };
}

/** An instant as a short day — the queue's column — or nothing. */
function shortDay(value: unknown): string {
  if (typeof value !== 'string') return '';
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? '' : at.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Whether an instant falls on the viewer's today. */
function sameDayAsNow(value: string): boolean {
  const at = new Date(value);
  return !Number.isNaN(at.getTime()) && sameDay(at, new Date());
}

/** What a completion said above the queue, with what *Undo* reopens it under. */
interface ICompleted {
  name: string;
  score: string;
  interviewId: string;
  etag: string;
}

export function DdCallsPage() {
  const [params, setParams] = useSearchParams();
  // SR-499: the surveys chosen — one reads its own catalogue and view, several `all` joined with theirs.
  const chosenSurveys = surveysParam(params);
  const surveysKey = chosenSurveys.join('+');
  const whose = whoseOf(params);
  const show = showOf(params);
  const states = statesOf(params);
  const statesKey = JSON.stringify(states);
  // The search and the page's own filters, and the window — the page and the rows *Load more*
  // added — in the URL.
  const { q, set, search: searched } = useWorkSearch();
  const paging = useWorkPaging({ replace: true });
  const { page, perPage, pages } = paging;
  const [ticked, setTicked] = React.useState<ReadonlySet<string>>(new Set());
  const [opened, setOpened] = React.useState<Nullable<IListRow>>(null);
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const [completed, setCompleted] = React.useState<Nullable<ICompleted>>(null);
  // The last *Assign all*, numbered so each draws a fresh line and a fresh Undo.
  const [assignedAll, setAssignedAll] = React.useState<Nullable<{ done: IAssignedAll; n: number }>>(null);
  // Moved by an *Undo* from above the queue: the panel's block reads again.
  const [blockVersion, setBlockVersion] = React.useState(0);
  // Moved by every write and Refresh: the quick views' counts are read again then, not per page.
  const [recount, setRecount] = React.useState(0);
  // Each row's last re-read (`rereadRow`), by row key: only the last one's answer lands.
  const rereads = React.useRef(new Map<string, number>());
  // Each survey's seeded DD list — read first: the surveys that hold one are what the rest reads.
  const listRead = useAdminData<{ key: string; dd: DdList }>(async (signal) => {
    const read = await Promise.all(
      chosenSurveys.map(async (code): Promise<Nullable<IDdView>> => {
        const views = await listsApi.listViews(code, 'list', signal);
        const seed = views?.find((view) => view.seed === 'dd_list');
        if (!seed) return null;
        const view = await listsApi.openView(seed.id, signal);
        return view ? { survey: code, viewId: seed.id, query: view.body.query } : null;
      })
    );
    const views = read.filter((view): view is IDdView => view !== null);
    if (views.length === 0) return { key: surveysKey, dd: 'none' };
    return {
      key: surveysKey,
      dd:
        views.length === 1
          ? { viewId: views[0].viewId, query: views[0].query, views }
          : { viewId: null, query: joinedQuery(views), views }
    };
  }, [surveysKey]);
  // The answer of the surveys chosen now, never the last choice's.
  const list = {
    data: listRead.data?.key === surveysKey ? listRead.data.dd : null,
    error: listRead.error,
    reload: listRead.reload
  };
  // SR-499: the surveys read — the chosen ones holding a DD list on this deployment (no Canada
  // where none is held); one reads its own catalogue and view, several `all` joined with theirs.
  const held = list.data && list.data !== 'none' ? list.data.views.map((view) => view.survey) : null;
  const surveys = held ?? chosenSurveys;
  const multi = surveys.length > 1;
  const survey = multi ? ALL_SURVEYS.code : surveys[0];
  const readKey = surveys.join('+');
  // The catalogue words each cell as the Lists grid does: an option's label, an instant, a person.
  // A catalogue read for another survey is none: a survey switched never words its cells by the last.
  // Several surveys wait for their DD lists, so the catalogue never names a survey not held.
  const fieldsRead = useAdminData(
    async (signal) => ({
      readKey,
      fields:
        chosenSurveys.length > 1 && held === null
          ? null
          : await listsApi.fields(survey, signal, multi ? surveys : undefined)
    }),
    [readKey, chosenSurveys.length > 1 && held === null]
  );
  const catalogue = fieldsRead.data?.readKey === readKey ? fieldsRead.data.fields : null;
  const unread = React.useMemo<IListFieldCatalogue>(() => ({ survey, groups: [] }), [survey]);
  // Lists' controls beside the queue's own: a filter, a sort that replaces the view's, extra columns.
  const work = useWorkPage(survey, catalogue ?? unread, { replace: true, drop: PAGING });
  const can = useCan();
  const canAssign = can('internal_fields.write') && can('lists.assign');

  // What was ticked and said belongs to the list it was said over.
  React.useEffect(() => {
    setTicked((now) => (now.size > 0 ? new Set() : now));
    setSaid(null);
  }, [surveysKey, whose, show, statesKey, q, page, work.filter, work.sorts]);
  // An *Assign all*'s outcome, and its Undo, belong to the survey it was written in.
  React.useEffect(() => {
    setOpened(null);
    setAssignedAll(null);
  }, [surveysKey]);


  const dd = list.data !== null && list.data !== 'none' ? list.data : null;
  /** A row's own survey's DD view — what its writes name. */
  const viewOf = (code: string) => dd?.views.find((view) => view.survey === code) ?? null;
  /** A survey's Quality rating code, as its seeded view names it — the exception's field. */
  const qualityOf = (code: string) =>
    viewOf(code)?.query.columns?.find((field) => field.endsWith('.quality_rating')) ?? null;
  // The team's columns per survey, changed by the Data team and Administrators (SR-500).
  const fixed = [...FIXED, ...(dd?.query.columns ?? [])];
  const canColumns = can('work_pages.columns');
  // Said once with the assignment's sentence where the roles lack both.
  const chosen = useDdColumns(
    work,
    fixed,
    surveys,
    canColumns,
    canAssign ? COLUMNS_REASON : ASSIGN_REASON,
    catalogue !== null
  );
  /** The surveys a survey's own field — a question, a staff field — belongs to: the one whose staff
   *  prefix it carries, else those whose layout added it; none for a field every row holds. */
  const ownersOf = (code: string): string[] => {
    const source = work.sourceOf(code);
    if (source !== 'answer' && source !== 'internal') return [];
    const prefix = code.slice(0, code.indexOf('.') + 1);
    const staff = surveys.filter((one) => qualityOf(one)?.startsWith(prefix));
    return staff.length > 0 ? staff : chosen.addedBy(code);
  };
  const showLeaves = SHOWS.find((entry) => entry.value === show)?.leaves ?? [];
  const leaves = [...showLeaves, ...stateLeaves(states)];
  const filtered = work.filter ? [work.filter] : [];
  // The queue's query as its rows are read — what *Assign all* sends, too — Lists' filter ANDed on,
  // and a column's sort in place of the view's.
  const queue = dd
    ? {
        ...narrowed(dd.query, whose, q, [...leaves, ...filtered]),
        ...(work.sorts.length > 0 ? { sorts: work.sorts } : {})
      }
    : null;
  // The funnels count under the queue's own narrowing, the filter's tree apart.
  const scope: IWorkScope = {
    query: queue ?? { survey },
    base: dd ? (narrowed(dd.query, whose, q, leaves).criteria ?? null) : null
  };
  // The rows are Lists' own read (`useListRows`, unpolled): a result drawn only under the query
  // it answers, a failure said only under its own query, a save's cells adopted on the page.
  const rowColumns = [
    ...(dd?.query.columns ?? []),
    CYCLE,
    SCHEDULED,
    SCHEDULED_BY,
    SCHEDULE_NOTE,
    NEXT_MEETING_AT,
    NEXT_MEETING_KIND,
    DISCLOSURES_CLEARED,
    ...DD_DISCLOSURE_COLUMNS,
    ...chosen.extras
  ];
  const listRows = useListRows({
    query: dd && queue ? { ...queue, columns: rowColumns } : { survey },
    page,
    pageSize: perPage,
    pages,
    sums: [],
    enabled: dd !== null,
    polled: false
  });
  // The last answer while the next is out, dimmed — of this survey; none once this query's read failed.
  const rows = listRows.read && answersSurveys(listRows.read.asked, surveys) ? listRows.read : null;
  const readError = listRows.error?.message ?? null;

  // Each quick view's count under the same whose, search, states and filter — but the signals
  // pressed, which narrow the rows alone — and its signals: survey-api's, one read of them all, again
  // after a write or a Refresh (never per page: the page is not in what they count).
  const [variant] = useCardVariant();
  const countedFilter = useCountedFilter(work, DD_SIGNALS);
  const pressed = signalsOn(work, DD_SIGNALS).map((one) => one.key);
  const cards = useDdCards(
    SHOWS,
    dd ? narrowed(dd.query, whose, q, [...stateLeaves(states), ...(countedFilter ? [countedFilter] : [])]) : null,
    whose === 'all',
    variant,
    JSON.stringify([surveysKey, whose, q, statesKey, countedFilter, recount, dd?.views.map((view) => view.viewId) ?? null]),
    (card, key) => card === show && pressed.includes(key)
  );
  const countsShown = cards?.counts ?? null;

  // The states present under every other narrowing — whose, search, quick view — each with its
  // count: survey-api's `/aggregates` grouped by state, a filter's own choice left out of what it
  // counts. Its own read, since the quick views' counts leave the quick view out instead; read again
  // with them.
  const stateCounted = JSON.stringify([surveysKey, whose, q, show, work.filter]);
  const stateCounts = useAdminData<Nullable<{ counted: string; by: IStateCount[] }>>(
    async (signal) => {
      if (!dd) return null;
      const answer = await listsApi.aggregates(
        {
          query: { ...narrowed(dd.query, whose, q, [...showLeaves, ...filtered]), groupBy: [{ field: STATE }] },
          fields: []
        },
        signal
      );
      const by = answer.groups
        .map((group) => ({ state: group.keys[0], count: group.count }))
        .filter((entry): entry is IStateCount => typeof entry.state === 'string' && entry.state !== '');
      return { counted: stateCounted, by };
    },
    [dd, stateCounted, recount]
  );
  const stateCountsShown = stateCounts.data?.counted === stateCounted ? stateCounts.data.by : null;

  // Read only for a reader who may assign: every control it feeds is off for the rest.
  const accounts = useAdminData(
    async (signal) => (canAssign ? listsApi.assignees(signal) : null),
    [canAssign]
  );
  const fields = work.fields;
  const text = (row: IListRow, code: string) => {
    const field = fields.get(code);
    return field ? cellText(field, row.cells[code]) : '';
  };
  const advisor = (row: IListRow) => text(row, NAME) || row.participantId;
  const place = (row: IListRow) => [text(row, 'sys.city'), text(row, 'sys.state')].filter(Boolean).join(', ');
  const statusOf = (row: IListRow) => String(row.cells[STATUS]?.v ?? 'none');
  const scheduledOf = (row: IListRow) => {
    const at = row.cells[SCHEDULED]?.v;
    return typeof at === 'string' ? at : null;
  };
  /** The row's one next step (A4), the panel's and the record chip's too. */
  const stepOf = (row: IListRow) => ddStep({ ...ddStepStateOf(row), u4: u4FlagOf(ddDisclosuresOf(row)) });

  // The panel's row as last read — its assignee's version moves with every write — else as opened,
  // once a write has taken it off this page.
  const drawn = rows?.rows ?? [];
  const panelRow = opened ? (drawn.find((row) => rowKeyOf(row) === rowKeyOf(opened)) ?? opened) : null;
  const panelIndex = panelRow ? drawn.findIndex((row) => rowKeyOf(row) === rowKeyOf(panelRow)) : -1;
  const tickedRows = drawn.filter((row) => ticked.has(rowKeyOf(row)));
  // One survey's Quality is a column of its own, sorted and filtered; several surveys' each row's own.
  const quality = multi ? null : qualityOf(survey);
  const qualityAny = dd?.views.some((view) => qualityOf(view.survey) !== null) ?? false;

  /** A cell as a write of this page left it — on the page (`adopt`, which also abandons a read
   *  begun before the write) and on the panel's held row, which no re-read brings back once the
   *  row is off this page — so a second write quotes the version the first got. */
  const adoptCell = (row: Pick<IListRow, 'participantId' | 'survey'>, field: string, cell: IListCell) => {
    // A re-read of the row out since before this write would put the cell back as it was.
    rereads.current.set(rowKeyOf(row), (rereads.current.get(rowKeyOf(row)) ?? 0) + 1);
    listRows.adopt([{ participantId: row.participantId, survey: row.survey, field, cell }]);
    setOpened((held) =>
      held && rowKeyOf(held) === rowKeyOf(row) ? { ...held, cells: { ...held.cells, [field]: cell } } : held
    );
  };
  /** An *Assign all*'s cells, or its undo's, adopted as `adoptCell` does one: on the page and on
   *  the panel's held row, if among them. */
  const adoptAssigned = (cells: readonly IAssignedCell[]) => {
    const adopted = new Map(
      cells.map(({ ref, accountId }) => [
        rowKeyOf(ref),
        {
          participantId: ref.participantId,
          survey: ref.survey,
          field: ASSIGNED_TO,
          cell: {
            v: accountId,
            version: ref.version,
            label: accountId === null ? null : (accounts.data?.find((a) => a.accountId === accountId)?.email ?? null)
          }
        }
      ])
    );
    listRows.adopt([...adopted.values()]);
    setOpened((held) => {
      const own = held && adopted.get(rowKeyOf(held));
      return held && own ? { ...held, cells: { ...held.cells, [ASSIGNED_TO]: own.cell } } : held;
    });
  };
  /** After a write: the rows and the counts read again. */
  const moved = () => {
    setRecount((count) => count + 1);
    void listRows.refresh();
  };

  /**
   * After a write from the panel or the sheet (A5): that one pair read again — its participant on
   * its survey, whoever it is assigned to and whatever the queue's narrowing — and its cells put in
   * place, on the page and on the panel's held row; the counts read again. The row stays where it
   * is drawn, as a poll's patch does, even where the write took it off the list: *Refresh* reads
   * the page afresh. A failed read leaves the row as drawn, and so does one overtaken by a later
   * write to the row: two writes' re-reads may land out of order, and the older would draw the row
   * as it was before the newer.
   */
  const rereadRow = async (row: Pick<IListRow, 'participantId' | 'survey'>) => {
    setRecount((count) => count + 1);
    if (!queue) return;
    const key = rowKeyOf(row);
    const mine = (rereads.current.get(key) ?? 0) + 1;
    rereads.current.set(key, mine);
    try {
      const answer = await listsApi.rows({
        query: {
          survey: queue.survey,
          surveys: queue.surveys,
          columns: rowColumns,
          criteria: {
            version: 1,
            op: 'and',
            // `sys.survey` exists only on the several-survey list; one survey's list is the survey.
            children: [
              { field: PARTICIPANT, op: 'eq', value: row.participantId },
              ...(queue.surveys ? [{ field: SURVEY, op: 'eq' as const, value: row.survey }] : [])
            ]
          }
        },
        page: 1,
        // survey-api's least page; the pair is one row.
        pageSize: LIST_PAGE_SIZES[0]
      });
      const fresh = answer.rows.find((one) => rowKeyOf(one) === key);
      if (!fresh || rereads.current.get(key) !== mine) return;
      // A cell the read holds no more is one emptied by the write.
      const codes = new Set([...rowColumns, ...Object.keys(fresh.cells)]);
      const { participantId, survey: code } = row;
      listRows.adopt([...codes].map((field) => ({ participantId, survey: code, field, cell: fresh.cells[field] ?? null })));
      setOpened((held) => (held && rowKeyOf(held) === rowKeyOf(row) ? { ...held, cells: fresh.cells } : held));
    } catch {
      // The row as drawn; *Refresh* reads it again.
    }
  };

  /** One batch of the Lists' assignee cells; the sentence to say, and the list read again. */
  const assign = async (targets: readonly IListRow[], accountId: Nullable<string>): Promise<string> => {
    if (!dd) return 'The DD list is not read yet.';
    try {
      const answer = await listsApi.edit({
        // The view the batch is read from — none where its rows span several surveys' views.
        viewId:
          dd.viewId ??
          (new Set(targets.map((row) => row.survey)).size === 1 ? viewOf(targets[0].survey)?.viewId : null),
        cells: targets.map((row) => ({
          participantId: row.participantId,
          survey: row.survey,
          field: ASSIGNED_TO,
          value: accountId,
          version: row.cells[ASSIGNED_TO]?.version ?? 0
        }))
      });
      const email = accountId === null ? null : (accounts.data?.find((a) => a.accountId === accountId)?.email ?? null);
      const settled = [...answer.applied, ...answer.unchanged];
      const conflicts = answer.conflicts.length;
      setTicked((now) => {
        const left = new Set(now);
        for (const cell of settled) left.delete(rowKeyOf(cell));
        return left;
      });
      for (const cell of answer.applied) adoptCell(cell, ASSIGNED_TO, { v: accountId, version: cell.version, label: email });
      // Read again only where something moved: a write, or a colleague's assignment to show.
      if (answer.applied.length + conflicts > 0) moved();
      const whom = accountId === null ? 'nobody' : (email ?? 'the account chosen');
      return [
        settled.length > 0 && `${countOf(settled.length, 'row')} assigned to ${whom}.`,
        conflicts > 0 &&
          `${countOf(conflicts, 'row')} assigned by a colleague since the list was read, and left as ${conflicts === 1 ? 'it is' : 'they are'}.`
      ]
        .filter(Boolean)
        .join(' ');
    } catch (error) {
      return refusalMessage(error, 'The assignment could not be saved.');
    }
  };

  /**
   * Refresh, the Rankings record's and Lists' own (`RefreshButton`): everything the page draws — the rows (and with them the
   * counts, which re-read on every page read), the accounts and the open panel's block — read
   * afresh; a re-read that landed clears what the last write was told, and one that failed over a
   * drawn list keeps it, said as last read. The seeded view itself is read on a survey change.
   */
  const readAgain = async () => {
    setBlockVersion((count) => count + 1);
    setRecount((count) => count + 1);
    if (accounts.error) void accounts.reload();
    // A view read that failed is read again, and its answer reads the rows by itself — so what the
    // last write was told stays until a read of the rows has landed.
    if (list.error) {
      await list.reload();
      return;
    }
    if ((await listRows.refresh()) !== null) setSaid(null);
  };

  const [undoing, setUndoing] = React.useState(false);
  const undo = async () => {
    if (!completed || undoing) return;
    setUndoing(true);
    try {
      await ddApi.reopen(completed.interviewId, completed.etag, 'Undone on DD calls straight after completing.');
      setSaid(`Undone: ${completed.name}'s DD call is open again and back on the DD list.`);
      setCompleted(null);
      setBlockVersion((count) => count + 1);
      moved();
    } catch (error) {
      setSaid(refusalMessage(error, 'The DD call could not be reopened.'));
    } finally {
      setUndoing(false);
    }
  };

  // Off without the roles, or without the accounts to choose from.
  const assignOff = !canAssign || accounts.data === null;
  const accountsUnread = accounts.error !== null;
  const compact = panelRow !== null;
  const head = workHeads(work, scope);

  const columns: IDataColumn<IListRow>[] = [
    {
      id: 'name',
      ...head(NAME, 'Advisor'),
      cell: (row) => (
        <Link className="font-medium text-[var(--color-royal-blue)] hover:underline" {...recordLink(row)}>
          {advisor(row)}
        </Link>
      )
    },
    // SR-499: several surveys' rows, each saying its own.
    ...(multi
      ? [{ id: 'survey', ...head(SURVEY, 'Survey'), cell: (row: IListRow) => listSurveyLabel(row.survey) }]
      : []),
    { id: 'firm', ...head(FIRM, 'Firm'), cell: (row: IListRow) => text(row, FIRM) },
    {
      id: 'place',
      header: 'City, state',
      cell: place
    },
    // The rating a DD call writes, so on the DD list it is always the last cycle's (B3).
    ...(quality
      ? [{ id: 'quality', ...head(quality, QUALITY_LAST), cell: (row: IListRow) => text(row, quality) }]
      : multi && qualityAny
        ? [
            {
              id: 'quality',
              header: QUALITY_LAST,
              cell: (row: IListRow) => {
                const own = qualityOf(row.survey);
                return own ? text(row, own) : '';
              }
            }
          ]
        : []),
    {
      id: 'status',
      ...head(STATUS, 'DD status'),
      // The code, not its label: the chip words it.
      cell: (row) => <DdStatusChip status={statusOf(row)} />
    },
    {
      id: 'next',
      header: 'Next step',
      cell: (row) => {
        const step = stepOf(row);
        // The wide table's Call scheduled column dates the DD call: the step says only what to do.
        const words = !compact && step.kind === 'dd_call' && step.at ? step.label : step.summary;
        return (
          <span className="flex items-center gap-1.5 whitespace-nowrap" data-dd-step={step.action}>
            <span className={step.today ? 'font-medium' : undefined}>{words}</span>
            {/* The DD call's Overdue is the Call scheduled column's on the wide table. */}
            {step.overdue && (compact || step.kind !== 'dd_call') && <DdOverdueChip />}
          </span>
        );
      }
    },
    {
      id: 'meetings',
      ...head(NEXT_MEETING_AT, 'Meetings'),
      // A1: the cycle's soonest booking, of any kind — the DD call's included.
      cell: (row: IListRow) => {
        const next = nextMeetingOf(row);
        if (!next) return null;
        return (
          <span className="whitespace-nowrap" title={ddWhen(next.at)} data-dd-next-meeting={next.kind}>
            {/* The day alone, or today's time; the whole instant on hover. */}
            {meetingShortLabel(next.kind)} · {sameDayAsNow(next.at) ? ddWhenShort(next.at) : shortDay(next.at)}
          </span>
        );
      }
    },
    {
      id: 'scheduled',
      ...head(SCHEDULED, 'Call scheduled'),
      cell: (row: IListRow) => {
        const at = scheduledOf(row);
        if (!at) return null;
        const by = text(row, SCHEDULED_BY);
        const note = text(row, SCHEDULE_NOTE);
        const overdue = callOverdue(at, statusOf(row));
        return (
          <span className="grid gap-0.5" data-dd-scheduled={at}>
            <span className="flex flex-wrap items-center gap-x-1.5 whitespace-nowrap">
              {ddWhen(at)}
              {by && (
                <span className="text-muted-foreground" title={by}>
                  · {by.split('@')[0]}
                </span>
              )}
              {overdue && <DdOverdueChip />}
            </span>
            {/* One line, its words whole in the tooltip and in the panel. */}
            {note && (
              <span className="max-w-64 truncate text-muted-foreground" title={note}>
                {note}
              </span>
            )}
          </span>
        );
      }
    },
    {
      id: 'disclosures',
      header: 'U4',
      // SR-498: the U4 flag — issue found, clear, not checked — a link to the record's disclosures.
      cell: (row: IListRow) => <U4FlagLink facts={ddDisclosuresOf(row)} bare />
    },
    { id: 'outreach', ...head(OUTREACH, 'Outreach'), numeric: true, cell: (row) => text(row, OUTREACH) || '0' },
    {
      id: 'last',
      ...head(LAST_OUTREACH, 'Last outreach'),
      cell: (row) => (
        <span className="whitespace-nowrap" title={text(row, LAST_OUTREACH) || undefined}>
          {shortDay(row.cells[LAST_OUTREACH]?.v)}
        </span>
      )
    },
    {
      id: 'assigned',
      ...head(ASSIGNED_TO, 'Assigned to'),
      cell: (row) => {
        const email = text(row, ASSIGNED_TO);
        // The address's name part: the whole address is the tooltip, and the panel's select.
        return (
          <span className="whitespace-nowrap" title={email || undefined}>
            {email.split('@')[0]}
          </span>
        );
      }
    },
    {
      id: 'open',
      header: <span className="sr-only">Details</span>,
      label: 'Details',
      cell: (row) => (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label={`Open ${advisor(row)} beside the list`}
          aria-pressed={panelRow !== null && rowKeyOf(panelRow) === rowKeyOf(row)}
          onClick={() => setOpened(row)}
        >
          <PanelRight className="size-4" aria-hidden />
        </Button>
      )
    }
  ];

  // The viewer's order and choice; beside an open panel the queue keeps what a researcher works by.
  // The row's Details button stays last, after any field the team added.
  // The row's Details button is no column the team arranges: drawn last, after any field added.
  const arranged = columns.filter((column) => column.id !== 'open');
  const shown = [...chosen.resolve(arranged, scope), ...columns.filter((column) => column.id === 'open')];
  if (multi) {
    // Several surveys: their Survey column beside the advisor, whatever a survey's layout knew of it…
    const at = shown.findIndex((column) => column.id === 'survey');
    if (at > 1) shown.splice(1, 0, ...shown.splice(at, 1));
    // …and a survey's own field — its question or staff field — named with its survey, as FA's and
    // Teams' Approval Status are two fields under two codes; the others' rows hold none of it.
    for (const [index, column] of shown.entries()) {
      const owners = chosen.extras.includes(column.id) ? ownersOf(column.id) : [];
      if (owners.length === 0) continue;
      const label = `${fields.get(column.id)?.label ?? column.id} (${owners.map(listSurveyLabel).join(', ')})`;
      shown[index] = {
        ...column,
        label,
        header: <WorkHeader page={work} scope={scope} code={column.id} label={label} />
      };
    }
  }

  // B1: *Assigned to me* empty under no other narrowing offers the rest of the list, counted. The
  // cards count under *Whose*, so they hold no number of the others' rows: one counts read, sent
  // only while this empty state is drawn.
  const narrowedAny = q !== '' || show !== 'all' || states.length > 0 || work.filter !== null;
  const mineEmpty = dd !== null && whose === 'mine' && !narrowedAny && rows !== null && rows.total === 0;
  const othersCounted = JSON.stringify([surveysKey, recount]);
  const others = useAdminData<Nullable<{ counted: string; counts: Record<string, number> }>>(
    async (signal) => {
      if (!dd || !mineEmpty) return null;
      const { criteria } = narrowed(dd.query, 'all', '', []);
      const answer = await listsApi.counts(
        {
          query: { survey: dd.query.survey, surveys: dd.query.surveys, scope: dd.query.scope, criteria },
          counts: [
            { key: 'all', criteria: null },
            { key: 'unassigned', criteria: { version: 1, op: 'and', children: [{ field: ASSIGNED_TO, op: 'empty' }] } }
          ]
        },
        signal
      );
      return { counted: othersCounted, counts: answer.counts };
    },
    [mineEmpty, othersCounted]
  );
  const othersShown = others.data?.counted === othersCounted ? others.data.counts : null;

  const state: DataTableState | undefined =
    (list.error && list.data === null) || (readError && rows === null)
      ? { kind: 'error', content: <LoadError message={list.error ?? readError ?? ''} /> }
      : list.data === 'none'
        ? { kind: 'empty', content: 'This survey has no DD list: its catalogue lacks its Travel or DD fields.' }
        : rows === null
          ? { kind: 'loading' }
          : rows.rows.length === 0
            ? {
                kind: 'empty',
                content: narrowedAny
                  ? 'Nobody on the DD list matches.'
                  : whose === 'mine'
                    ? <MineEmpty counts={others.error ? NO_COUNTS : othersShown} onWhose={(next) => set({ whose: next })} />
                    : whose === 'unassigned'
                      ? 'Everybody on the DD list is assigned.'
                      : 'Nobody is on the DD list.'
              }
            : undefined;

  // `?open=<participantId>` (My tasks' link, SR-424) opens that row's panel once its page is drawn,
  // then leaves the address, so a later close is not undone by it.
  const openParam = params.get('open');
  React.useEffect(() => {
    if (openParam === null || !rows) return;
    const row = drawn.find((one) => one.participantId === openParam);
    if (row) setOpened(row);
    const next = new URLSearchParams(params);
    next.delete('open');
    setParams(next, { replace: true });
  }, [openParam, rows, drawn, params, setParams]);

  // The focused row is the panel's, while a panel is open: ↑/↓ in the table walk it.
  const follow = (event: React.FocusEvent) => {
    if (!panelRow) return;
    const key = (event.target as HTMLElement).closest('tr')?.getAttribute('data-row-key');
    const row = key ? drawn.find((one) => rowKeyOf(one) === key) : undefined;
    if (row && rowKeyOf(row) !== rowKeyOf(panelRow)) setOpened(row);
  };

  // Where the page's own controls end: scrolled under the app bar, the compact bar stands for them.
  const [controlsEnd, setControlsEnd] = React.useState<Nullable<HTMLElement>>(null);
  const scrolled = useScrolledPast(controlsEnd);

  /** The surveys chosen into the address, one key each: the rest of the queue's narrowing kept,
   *  but a column's filter and sort, whose fields the next catalogue may not hold. */
  const chooseSurveys = (next: string[]) => {
    const moved = dropPaging(new URLSearchParams(params));
    moved.delete('survey');
    for (const code of next) moved.append('survey', code);
    moved.delete('f');
    moved.delete('sort');
    setParams(moved, { replace: true });
  };
  const shownEntry = SHOWS.find((entry) => entry.value === show) ?? SHOWS[0];

  // The controls drawn twice — in the page, and slimmed in the compact bar once they scroll away.
  // One survey's view opens in Lists; several surveys' joined list is no view of Lists'.
  const lists = dd?.viewId
    ? {
        to: `${TEAM_PATHS.lists}?survey=${encodeURIComponent(survey)}&view=${dd.viewId}`,
        tooltip: 'The DD list in Lists, where you can divide its rows and assign them.'
      }
    : null;
  const refresh = {
    reading: listRows.reading,
    readAt: listRows.read?.readAt ?? null,
    onClick: () => void readAgain()
  };
  const help = (
    <HelpPopover label="About the DD list" align="end">
      <p>
        Advisors eligible to travel whose survey is submitted and whose DD call box is not ticked. A completed
        DD call ticks it, and the tick holds through later cycles until someone unticks it; an advisor with a
        draft joins once they submit.
      </p>
      <p>
        The next step follows the cadence: three DD emails, then a call; a booked meeting of any kind comes
        first. Meetings shows the soonest one. Quality is the rating the last cycle&apos;s DD call wrote. Open a
        row beside the list to log outreach and run the interview, or its name for the advisor&apos;s record.
      </p>
      <p>
        Each card counts one view of the list under the filters; its badges count the rows in it that are
        overdue, unassigned, not yet emailed or meeting this week — press one to show only those, and another to show those too;
        press it again, or its ✕, to drop it — the counts stay as they are. Hover a card for what it holds.
      </p>
      <p>
        Scrolled down, the header, the cards and the filters ride slimmed in the bar at the top of the window,
        with the table’s column names under them.
      </p>
    </HelpPopover>
  );
  const showCards = (
    <StateCards
      label="Show"
      compact
      cards={SHOWS.map((entry) => ({
        key: entry.value,
        to: { search: withShow(params, entry.value).toString() },
        replace: true,
        current: show === entry.value,
        label: entry.label,
        count: countsShown?.[entry.value]?.toLocaleString('en-US') ?? '…',
        hint: entry.hint,
        tooltip: `${entry.full ?? entry.label} — ${entry.hint}`,
        signals: toggledSignals({
          page: work,
          params,
          setParams: (next) => setParams(dropPaging(next), { replace: true }),
          current: show === entry.value,
          signals: cards?.signals[entry.value],
          specs: DD_SIGNALS,
          pressed,
          openCard: (next) => (entry.value === 'all' ? next.delete('show') : next.set('show', entry.value)),
          nameOf: (signal) =>
            `Show ${signal.count.toLocaleString('en-US')} ${signal.label} in ${entry.full ?? entry.label}`
        }),
        share: countsShown && entry.value !== 'all' && countsShown.all > 0
          ? (countsShown[entry.value] ?? 0) / countsShown.all
          : undefined,
        data: { 'data-show': entry.value },
        countData: { 'data-count': '' }
      }))}
    />
  );
  const search = {
    label: 'Filter advisors',
    placeholder: 'Filter advisors…',
    filters: true,
    ...searched
  };
  const whoseChip = (
    <SelectChip
      label="Whose"
      any="Everyone"
      // *Everyone* narrows nothing; *Assigned to me*, the default, is the address without `whose`.
      value={whose === 'all' ? null : whose}
      options={WHOSE.filter((entry) => entry.value !== 'all')}
      onChoose={(value) => set({ whose: value === null ? 'all' : value === 'mine' ? null : value })}
    />
  );
  const stateFilter = (
    <StateFilter
      chosen={states}
      counts={stateCountsShown}
      onChoose={(next) => set({ states: next })}
    />
  );
  const assignAll = queue && (
    <AssignAll
      query={queue}
      viewId={dd?.viewId}
      count={rows?.total ?? null}
      noun={ADVISORS}
      accounts={accounts.data}
      disabled={assignOff}
      describedBy={!canAssign ? ASSIGN_REASON : undefined}
      onAssigned={(done) => {
        setSaid(null);
        setTicked(new Set());
        setAssignedAll((last) => ({ done, n: (last?.n ?? 0) + 1 }));
        adoptAssigned(done.applied.map((ref) => ({ ref, accountId: done.accountId })));
        moved();
      }}
    />
  );
  const clearAll = () => set(Object.fromEntries(CLEARED.map((key) => [key, null])));
  // Every filter applied, as a chip with its value — the page's row under the toolbar and the
  // compact bar's second row alike: State's press opens its control, a Lists chip its column's
  // funnel.
  const toolbar = useWorkToolbar();
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const openFilters = () => {
    setFiltersOpen(true);
    return true as const;
  };
  const statesSaid = statesSummary(states);
  const quickChipsWith = (open: (label: string) => true): ICompactChip[] =>
    statesSaid === null ? [] : [quickChip('State', statesSaid, open, () => set({ states: [] }))];
  const appliedWith = (open: (label: string) => true) =>
    catalogue ? workFilterChips(work, DD_SIGNALS, quickChipsWith(open)) : quickChipsWith(open);
  const quickCount = quickChipsWith(openFilters).length;
  const appliedChips = appliedWith(openFilters);
  const pageChips = appliedWith(toolbar.openQuick);
  const showItems = SHOWS.map((entry) => ({
    key: entry.value,
    label: entry.label,
    to: { search: withShow(params, entry.value).toString() },
    replace: true,
    current: show === entry.value,
    count: countsShown?.[entry.value]?.toLocaleString('en-US') ?? '…'
  }));
  const columnsButton = chosen.button(arranged);

  const panelQuality = panelRow ? qualityOf(panelRow.survey) : null;

  return (
    <>
      <PageHeader
        title="DD calls"
        row
        tabs={<DdSurveyToggles chosen={chosenSurveys} onChoose={chooseSurveys} />}
        actions={
          <WorkPageHeaderActions
            summary={rows ? `${rows.total.toLocaleString('en-US')} on the DD list` : null}
            summaryData={{ role: 'status' }}
            lists={lists}
            refresh={refresh}
            help={help}
          />
        }
      />
      <CompactBar
        shown={scrolled && dd !== null}
        label="DD calls, compact"
        actions={<WorkPageHeaderActions summary={null} lists={lists} refresh={refresh} help={help} />}
        below={appliedChips.length > 0 && <CompactChipLine chips={appliedChips} onClearAll={clearAll} />}
      >
        <CompactTitle>DD calls</CompactTitle>
        <DdSurveyPicklist chosen={chosenSurveys} onChoose={chooseSurveys} />
        <CompactMenu
          label="Show"
          chip
          trigger={
            <>
              {shownEntry.label}
              <span className="font-semibold tabular-nums">
                {showItems.find((item) => item.current)?.count}
              </span>
            </>
          }
          items={showItems}
        />
        <CompactDivider />
        <CompactSearch {...search} />
        {/* The scope says its value in the bar too, Everyone included. */}
        <CondensedScope value={EXPANDED}>{whoseChip}</CondensedScope>
        <CompactFilters count={quickCount} open={filtersOpen} onOpenChange={setFiltersOpen}>
          {stateFilter}
        </CompactFilters>
        {catalogue && <AdvancedLink onPress={() => work.setAdvanced({ open: true })} />}
        <span className="flex-1" />
        <CompactDivider />
        {assignAll}
        {columnsButton}
        <CompactDivider />
      </CompactBar>
      <div className="grid min-w-0 gap-3 px-6 py-4">
        {showCards}
        <WorkToolbar
          toolbar={toolbar}
          search={<WorkSearch search={search} />}
          lead={whoseChip}
          quick={stateFilter}
          quickCount={quickCount}
          // An icon, as in the compact bar, so the row holds at 1366 px (scheme D).
          after={
            catalogue && (
              <CondensedScope value={ICON}>
                <WorkAdvanced page={work} />
              </CondensedScope>
            )
          }
          end={
            <>
              <HelpPopover label={`About ${shownEntry.full ?? shownEntry.label}`} align="end">
                <p>
                  {shownEntry.full ?? shownEntry.label}: {shownEntry.hint}.
                </p>
                <p>
                  Whose, State and the column funnels narrow the list; the cards count under the same filters,
                  the signals pressed apart.
                </p>
              </HelpPopover>
              {assignAll}
              {columnsButton}
            </>
          }
        />
        {pageChips.length > 0 && <CompactChipLine label="Filters" chips={pageChips} onClearAll={clearAll} />}
        <div ref={setControlsEnd} aria-hidden data-controls-end="" className="-mt-3 h-0" />
        {/* One sentence for what the roles lack — the Columns button says its part on hover too. */}
        {(!canAssign || !canColumns) && (
          <NotPermitted
            id={!canAssign ? ASSIGN_REASON : COLUMNS_REASON}
            what={
              !canAssign && !canColumns
                ? 'assigning DD calls or changing their columns'
                : !canAssign
                  ? 'assigning DD calls'
                  : 'changing the DD calls columns'
            }
          />
        )}
        {/* Said on hovering the Columns button that is off; here for a screen reader alone. */}
        {canColumns && multi && (
          <span id={COLUMNS_MULTI_REASON} className="sr-only">
            {COLUMNS_MULTI_WORDS}
          </span>
        )}
        {readError && rows !== null && (
          <LoadError message={`Could not refresh — showing the DD list as last read. ${readError}`} />
        )}
        {completed && (
          <div
            role="status"
            data-dd-completed=""
            className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-[color-mix(in_srgb,var(--color-teal)_8%,white)] px-3 py-2 text-[0.8125rem]"
          >
            <span>
              <span className="font-medium">{completed.name}</span>: DD call completed
              {completed.score && <>, score {completed.score}</>}. The advisor left the DD list.
            </span>
            <Button type="button" variant="outline" size="sm" disabled={undoing} onClick={() => void undo()}>
              Undo
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setCompleted(null)}>
              Dismiss
            </Button>
          </div>
        )}
        {assignedAll && (
          <AssignAllSaid
            key={assignedAll.n}
            done={assignedAll.done}
            noun={ADVISORS}
            onChanged={(restored) => {
              adoptAssigned(restored);
              moved();
            }}
            onDismiss={() => setAssignedAll(null)}
          />
        )}
        {said && (
          <p role="status" className="text-[0.8125rem]" data-dd-assigned="">
            {said}
          </p>
        )}
        <SelectionBar
          count={tickedRows.length}
          onClear={() => setTicked(new Set())}
          stickyTop={STICKY_BAR_TOP}
        >
          <BulkAssign
            count={tickedRows.length}
            accounts={accounts.data}
            accountsUnread={accountsUnread}
            off={assignOff}
            locked={!canAssign}
            onAssign={async (accountId) => setSaid(await assign(tickedRows, accountId))}
          />
        </SelectionBar>
        <div
          className={cn(
            'grid items-start gap-3',
            panelRow && 'min-[80rem]:grid-cols-[minmax(0,1fr)_min(40vw,44rem)]'
          )}
        >
          <div className="grid min-w-0 gap-3" onFocus={follow}>
            <DataTable
              label="DD list"
              columns={
                compact
                  ? shown.filter((column) => !COMPACT_HIDES.has(column.id) && !chosen.extras.includes(column.id))
                  : shown
              }
              rows={rows?.rows ?? null}
              rowKey={rowKeyOf}
              rowProps={(row) => ({ 'data-row-key': rowKeyOf(row) }) as React.HTMLAttributes<HTMLTableRowElement>}
              onOpen={setOpened}
              selected={panelRow ? rowKeyOf(panelRow) : null}
              selectable={{ selected: ticked, onSelect: (keys) => setTicked(new Set(keys)), name: advisor }}
              state={state}
              busy={listRows.reading && rows !== null}
              stickyTop={STICKY_TABLE_TOP}
            />
            {rows && <WorkPager {...paging.pager} total={rows.total} busy={listRows.reading} sticky />}
          </div>
          {panelRow && (
            <DdPanel
              key={rowKeyOf(panelRow)}
              row={panelRow}
              name={advisor(panelRow)}
              firm={text(panelRow, 'sys.firm_name')}
              place={place(panelRow)}
              quality={panelQuality}
              qualityText={panelQuality ? text(panelRow, panelQuality) : ''}
              viewId={viewOf(panelRow.survey)?.viewId ?? null}
              accounts={accounts.data}
              accountsUnread={accountsUnread}
              off={assignOff}
              locked={!canAssign}
              blockVersion={blockVersion}
              previous={panelIndex > 0 ? () => setOpened(drawn[panelIndex - 1]) : null}
              next={panelIndex >= 0 && panelIndex < drawn.length - 1 ? () => setOpened(drawn[panelIndex + 1]) : null}
              onAssign={(accountId) => assign([panelRow], accountId)}
              onAdopt={(field, cell) => adoptCell(panelRow, field, cell)}
              onChanged={() => void rereadRow(panelRow)}
              onCompleted={(done) => {
                setSaid(null);
                setCompleted(done);
              }}
              onClose={() => setOpened(null)}
            />
          )}
        </div>
      </div>
    </>
  );
}

/** The counts of a read that failed: said by no number at all. */
const NO_COUNTS: Record<string, number> = {};

/** *Assigned to me* with nothing in it (B1): the way to everyone's rows and to the unassigned,
 *  each with its count — "…" while it is read, none where the read failed. */
function MineEmpty({
  counts,
  onWhose
}: {
  counts: Nullable<Record<string, number>>;
  onWhose: (whose: Whose) => void;
}) {
  const n = (key: string) => {
    const count = counts?.[key];
    return typeof count === 'number' ? ` (${count.toLocaleString('en-US')})` : counts === null ? ' (…)' : '';
  };
  return (
    <div className="grid justify-items-center gap-3" data-dd-mine-empty="">
      <span>Nobody on the DD list is assigned to you.</span>
      <span className="flex flex-wrap justify-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => onWhose('all')}>
          Show everyone{n('all')}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={() => onWhose('unassigned')}>
          Unassigned{n('unassigned')}
        </Button>
      </span>
    </div>
  );
}

/** A state present under the queue's narrowing, and how many rows hold it. */
interface IStateCount {
  state: string;
  count: number;
}

/**
 * The State filter (SR-459): every state the queue holds under its other narrowing, each with its
 * count, ticked many at once — and a shift-press ticks or clears the run from the last one pressed,
 * so Alabama to California is one gesture. A state chosen that the counts no longer hold is still
 * drawn, at 0, to be cleared.
 */
function StateFilter({
  chosen,
  counts,
  onChoose
}: {
  chosen: readonly string[];
  counts: Nullable<IStateCount[]>;
  onChoose: (states: string[]) => void;
}) {
  const last = React.useRef<Nullable<string>>(null);
  const listed = [
    ...(counts ?? []),
    ...chosen.filter((state) => !counts?.some((entry) => entry.state === state)).map((state) => ({ state, count: 0 }))
  ].sort((a, b) => a.state.localeCompare(b.state));
  const summary = statesSummary(chosen);

  const press = (state: string, range: boolean) => {
    const on = !chosen.includes(state);
    const from = range && last.current !== null ? listed.findIndex((entry) => entry.state === last.current) : -1;
    const to = listed.findIndex((entry) => entry.state === state);
    const run =
      from < 0 ? [state] : listed.slice(Math.min(from, to), Math.max(from, to) + 1).map((entry) => entry.state);
    const next = new Set(chosen);
    for (const one of run) {
      if (on) next.add(one);
      else next.delete(one);
    }
    last.current = state;
    onChoose(listed.map((entry) => entry.state).filter((one) => next.has(one)));
  };

  return (
    <FilterChip label="State" summary={summary}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-muted-foreground">
          {counts === null ? 'Counting…' : chosen.length > 0 ? `${chosen.length} chosen` : `${listed.length} states`}
        </span>
        {chosen.length > 0 && (
          <Button type="button" variant="ghost" size="sm" className="h-7" onClick={() => onChoose([])}>
            Clear
          </Button>
        )}
      </div>
      <div role="group" aria-label="States" className="grid max-h-72 min-w-48 gap-0.5 overflow-y-auto">
        {listed.map(({ state, count }) => {
          const on = chosen.includes(state);
          return (
            <button
              key={state}
              type="button"
              role="checkbox"
              aria-checked={on}
              data-state-choice={state}
              className="flex items-center gap-2 rounded px-1.5 py-1 text-left outline-none select-none hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
              onClick={(event) => press(state, event.shiftKey)}
            >
              <span
                aria-hidden
                className={cn(
                  'grid size-4 shrink-0 place-items-center rounded-sm border',
                  on ? 'border-[var(--color-teal)] bg-[var(--color-teal)] text-white' : 'border-border bg-white'
                )}
              >
                {on && <Check className="size-3" />}
              </span>
              <span className="flex-1">{state}</span>
              <span className="tabular-nums text-muted-foreground">{count.toLocaleString('en-US')}</span>
            </button>
          );
        })}
      </div>
    </FilterChip>
  );
}

/** The states chosen in a few words — three by name, the rest counted — none while none is. */
function statesSummary(chosen: readonly string[]): Nullable<string> {
  if (chosen.length === 0) return null;
  return chosen.length <= 3 ? chosen.join(', ') : `${chosen.slice(0, 3).join(', ')} +${chosen.length - 3}`;
}

/** The scope control drawn whole in the compact bar, its value said as in the page. */
const EXPANDED = { condensed: false };

/** *Advanced…* drawn as its icon in the page's toolbar, its words the tooltip. */
const ICON = { condensed: true };

/** The assignee select's value as the cell takes it. */
function accountOf(value: string): Nullable<string> {
  return value === NOBODY ? null : value;
}

/** The assignee choices — the Lists' assignable accounts and nobody. */
function AssigneeItems({ accounts }: { accounts: Nullable<IListAssignee[]> }) {
  return (
    <>
      <SelectItem value={NOBODY}>Nobody</SelectItem>
      {(accounts ?? []).map((account) => (
        <SelectItem key={account.accountId} value={account.accountId}>
          {account.email}
        </SelectItem>
      ))}
    </>
  );
}

/** The ticked rows' assignment, in the selection bar: an account chosen, then one press. */
export function BulkAssign({
  count,
  accounts,
  accountsUnread,
  off,
  locked,
  onAssign,
  reasonId = ASSIGN_REASON
}: {
  count: number;
  accounts: Nullable<IListAssignee[]>;
  accountsUnread: boolean;
  off: boolean;
  /** The reader's roles do not assign — said by the `NotPermitted` named `reasonId`. */
  locked: boolean;
  onAssign: (accountId: Nullable<string>) => Promise<void>;
  reasonId?: string;
}) {
  const [whom, setWhom] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const describedBy = locked ? reasonId : undefined;
  return (
    <div className="flex flex-wrap items-center gap-2 text-[0.8125rem]" role="group" aria-label="Assign the selected rows">
      <Select value={whom} onValueChange={setWhom} disabled={off}>
        <SelectTrigger className="h-8 w-64" aria-label="Assign the selected rows to" aria-describedby={describedBy}>
          <SelectValue placeholder="Assign to…" />
        </SelectTrigger>
        <SelectContent>
          <AssigneeItems accounts={accounts} />
        </SelectContent>
      </Select>
      <Button
        type="button"
        size="sm"
        disabled={off || busy || count === 0 || whom === ''}
        aria-describedby={describedBy}
        onClick={() => {
          setBusy(true);
          void onAssign(accountOf(whom)).finally(() => setBusy(false));
        }}
      >
        {busy ? 'Assigning…' : 'Assign'}
      </Button>
      {accountsUnread && <span className="text-muted-foreground">The accounts to assign could not be read.</span>}
    </div>
  );
}

/**
 * One row beside the queue: who it is, *Previous* / *Next*, its assignee (a select that writes on
 * choice, the work items' precedent), the link to the record's block, and the block itself, its
 * exception written through the row's Lists cell. Esc closes it by the split view's rule — never out of a field, a menu or the
 * interview's sheet.
 */
function DdPanel({
  row,
  name,
  firm,
  place,
  quality,
  qualityText,
  viewId,
  accounts,
  accountsUnread,
  off,
  locked,
  blockVersion,
  previous,
  next,
  onAssign,
  onAdopt,
  onChanged,
  onCompleted,
  onClose
}: {
  row: IListRow;
  name: string;
  firm: string;
  place: string;
  /** The survey's Quality rating code, the exception's field — null where the view holds none. */
  quality: Nullable<string>;
  qualityText: string;
  viewId: Nullable<string>;
  accounts: Nullable<IListAssignee[]>;
  accountsUnread: boolean;
  off: boolean;
  locked: boolean;
  blockVersion: number;
  previous: Nullable<() => void>;
  next: Nullable<() => void>;
  onAssign: (accountId: Nullable<string>) => Promise<string>;
  /** A cell the panel wrote, as survey-api answered it. */
  onAdopt: (field: string, cell: IListCell) => void;
  onChanged: () => void;
  onCompleted: (completed: ICompleted) => void;
  onClose: () => void;
}) {
  const titleId = React.useId();
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const [busy, setBusy] = React.useState(false);
  const cycleCell = row.cells[CYCLE]?.v;
  const cycle = typeof cycleCell === 'string' ? cycleCell : null;
  const assigneeCell = row.cells[ASSIGNED_TO]?.v;
  const assignee = typeof assigneeCell === 'string' && assigneeCell !== '' ? assigneeCell : null;
  // The panel's interview reads the survey's Quality score options off the staff form — one read
  // per survey per page load, shared with the record page.
  const schema = useAdminData(() => internalFieldsApi.schema(row.survey), [row.survey]);
  const disclosures = ddDisclosuresOf(row);
  useEscapeClose(onClose);

  return (
    <section
      aria-labelledby={titleId}
      data-dd-panel={row.participantId}
      className="grid content-start gap-4 rounded-md border border-border bg-card p-4 min-[80rem]:sticky min-[80rem]:top-16 min-[80rem]:max-h-[calc(100dvh-5rem)] min-[80rem]:overflow-y-auto"
    >
      <div className="flex items-start gap-3">
        <div className="grid min-w-0 flex-1 gap-0.5">
          <h2 id={titleId} className="truncate text-base font-medium text-[var(--color-royal-blue)]">
            {name}
          </h2>
          <span className="text-xs text-muted-foreground">
            {[firm, place, listSurveyLabel(row.survey), cycle && `cycle ${cycle}`, qualityText && `${QUALITY_LAST} ${qualityText}`]
              .filter(Boolean)
              .join(' · ')}
          </span>
          <Link
            {...recordLink(row)}
            className="text-sm font-medium text-[var(--color-sapphire)] underline-offset-4 hover:underline"
          >
            Open Due diligence on the record page
          </Link>
          {/* SR-498: the disclosures read before the call, beside who the advisor is. */}
          <div className="mt-1.5">
            <U4Check participantId={row.participantId} facts={disclosures} onChanged={onChanged} />
          </div>
        </div>
        <div className="flex items-center">
          <Button type="button" variant="ghost" size="icon" className="size-8" aria-label="Previous advisor" disabled={!previous} onClick={() => previous?.()}>
            <ChevronUp className="size-4" aria-hidden />
          </Button>
          <Button type="button" variant="ghost" size="icon" className="size-8" aria-label="Next advisor" disabled={!next} onClick={() => next?.()}>
            <ChevronDown className="size-4" aria-hidden />
          </Button>
          <Button type="button" variant="ghost" size="icon" className="size-8" aria-label="Close the details" onClick={onClose}>
            <X className="size-4" aria-hidden />
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3 text-[0.8125rem]">
        <div className="grid gap-1">
          <span className="font-medium">Assigned to</span>
          <Select
            value={assignee ?? NOBODY}
            disabled={off || busy}
            onValueChange={(value) => {
              setBusy(true);
              setSaid(null);
              void onAssign(accountOf(value))
                .then(setSaid)
                .finally(() => setBusy(false));
            }}
          >
            <SelectTrigger className="h-8 w-72" aria-label={`Assign ${name} to`} aria-describedby={locked ? ASSIGN_REASON : undefined}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/* An assignee the accounts do not offer (or not read) is still drawn, by the cell's label. */}
              {assignee && !accounts?.some((a) => a.accountId === assignee) && (
                <SelectItem value={assignee}>{row.cells[ASSIGNED_TO]?.label ?? assignee}</SelectItem>
              )}
              <AssigneeItems accounts={accounts} />
            </SelectContent>
          </Select>
        </div>
      </div>
      {said && (
        <p role="status" className="text-[0.8125rem]">
          {said}
        </p>
      )}
      <DdBlock
        participantId={row.participantId}
        surveyCode={row.survey}
        name={name}
        firm={firm || null}
        cycle={cycle}
        schema={schema.data}
        disclosures={disclosures}
        // *Mark as exception…* in the block's "⋯" and the sheet's *Couldn't complete…*: the Lists
        // cell write at the version this row was read with.
        exception={quality && viewId ? listCellException(row, viewId, quality, (cell) => onAdopt(quality, cell)) : undefined}
        checkAbove
        onChanged={onChanged}
        onCompleted={(interview: IDdInterview, etag: string) =>
          onCompleted({ name, score: qualityLabel(schema.data, interview.qualityScore), interviewId: interview.interviewId, etag })
        }
        version={blockVersion}
        heading="h3"
      />
    </section>
  );
}
